import React, { useState } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertCircleIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const ScenarioModellingDashboard: React.FC = () => {
  const [selectedScenario, setSelectedScenario] = useState('Base');
  const scenarios = ['Optimistic', 'Base', 'Pessimistic'];
  // KPI data changes based on scenario
  const scenarioKPIs = {
    Optimistic: {
      materialImpact: {
        value: '+2%',
        impact: '-0.6%',
        isPositive: false,
        subtitle: 'Material cost increase'
      },
      forexImpact: {
        value: '₹82.5',
        impact: '+0.2%',
        isPositive: true,
        subtitle: 'USD/INR rate'
      },
      marginSensitivity: {
        value: '18.8%',
        impact: '+0.5%',
        isPositive: true,
        subtitle: 'EBITDA margin'
      }
    },
    Base: {
      materialImpact: {
        value: '+5%',
        impact: '-1.5%',
        isPositive: false,
        subtitle: 'Material cost increase'
      },
      forexImpact: {
        value: '₹83.5',
        impact: '-0.3%',
        isPositive: false,
        subtitle: 'USD/INR rate'
      },
      marginSensitivity: {
        value: '17.8%',
        impact: '-0.5%',
        isPositive: false,
        subtitle: 'EBITDA margin'
      }
    },
    Pessimistic: {
      materialImpact: {
        value: '+10%',
        impact: '-3.0%',
        isPositive: false,
        subtitle: 'Material cost increase'
      },
      forexImpact: {
        value: '₹85.0',
        impact: '-1.2%',
        isPositive: false,
        subtitle: 'USD/INR rate'
      },
      marginSensitivity: {
        value: '16.3%',
        impact: '-2.0%',
        isPositive: false,
        subtitle: 'EBITDA margin'
      }
    }
  };
  const currentKPIs = scenarioKPIs[selectedScenario];
  // Margin Sensitivity Curve Data
  const marginSensitivityData = [{
    materialCostChange: -5,
    ebitdaMargin: 20.3
  }, {
    materialCostChange: -3,
    ebitdaMargin: 19.5
  }, {
    materialCostChange: -1,
    ebitdaMargin: 18.9
  }, {
    materialCostChange: 0,
    ebitdaMargin: 18.3
  }, {
    materialCostChange: 1,
    ebitdaMargin: 18.0
  }, {
    materialCostChange: 2,
    ebitdaMargin: 17.7
  }, {
    materialCostChange: 3,
    ebitdaMargin: 17.4
  }, {
    materialCostChange: 5,
    ebitdaMargin: 16.8
  }, {
    materialCostChange: 7,
    ebitdaMargin: 16.2
  }, {
    materialCostChange: 10,
    ebitdaMargin: 15.3
  }];
  // Cost Shock Impact Data
  const costShockImpactData = [{
    scenario: 'Optimistic',
    revenueImpact: 2.5,
    costImpact: -1.2,
    ebitdaImpact: 3.8,
    patImpact: 4.2,
    color: '#10b981'
  }, {
    scenario: 'Base',
    revenueImpact: 0.0,
    costImpact: 1.8,
    ebitdaImpact: -2.7,
    patImpact: -3.5,
    color: '#3b82f6'
  }, {
    scenario: 'Pessimistic',
    revenueImpact: -3.2,
    costImpact: 4.5,
    ebitdaImpact: -10.9,
    patImpact: -14.2,
    color: '#ef4444'
  }];
  // Scenario Result Summary Table
  const scenarioResultsData = [{
    scenario: 'Optimistic',
    materialCost: 2,
    forexRate: 82.5,
    revenue: 9481,
    cogs: 6532,
    grossProfit: 2949,
    grossMargin: 31.1,
    ebitda: 1782,
    ebitdaMargin: 18.8,
    pat: 1078,
    patMargin: 11.4,
    color: '#10b981'
  }, {
    scenario: 'Base',
    materialCost: 5,
    forexRate: 83.5,
    revenue: 9250,
    cogs: 6745,
    grossProfit: 2505,
    grossMargin: 27.1,
    ebitda: 1646,
    ebitdaMargin: 17.8,
    pat: 1000,
    patMargin: 10.8,
    color: '#3b82f6'
  }, {
    scenario: 'Pessimistic',
    materialCost: 10,
    forexRate: 85.0,
    revenue: 8954,
    cogs: 7046,
    grossProfit: 1908,
    grossMargin: 21.3,
    ebitda: 1460,
    ebitdaMargin: 16.3,
    pat: 889,
    patMargin: 9.9,
    color: '#ef4444'
  }];
  // Detailed Assumptions Table
  const assumptionsData = [{
    parameter: 'Material Cost Inflation',
    optimistic: '+2%',
    base: '+5%',
    pessimistic: '+10%',
    currentValue: '+3.2%'
  }, {
    parameter: 'USD/INR Exchange Rate',
    optimistic: '₹82.5',
    base: '₹83.5',
    pessimistic: '₹85.0',
    currentValue: '₹83.2'
  }, {
    parameter: 'Revenue Growth',
    optimistic: '+2.5%',
    base: '0.0%',
    pessimistic: '-3.2%',
    currentValue: '+1.2%'
  }, {
    parameter: 'Labour Cost Inflation',
    optimistic: '+4%',
    base: '+6%',
    pessimistic: '+8%',
    currentValue: '+5.5%'
  }, {
    parameter: 'Energy Cost Change',
    optimistic: '+3%',
    base: '+5%',
    pessimistic: '+8%',
    currentValue: '+4.2%'
  }, {
    parameter: 'Freight Cost Change',
    optimistic: '+2%',
    base: '+4%',
    pessimistic: '+7%',
    currentValue: '+3.8%'
  }];
  return <div data-id="element-4476">
      <DescriptionPanel title="Scenario Modelling - Material Cost & FOREX Impact" description="Advanced scenario planning tool to model the impact of material cost inflation and FOREX rate changes on financial performance. Analyze optimistic, base, and pessimistic scenarios to understand margin sensitivity and prepare strategic responses." insights={['Base scenario: +5% material cost reduces EBITDA margin to 17.8% from current 18.3%', 'FOREX sensitivity: ₹1 INR depreciation impacts PAT by approximately 0.4%', 'Pessimistic scenario requires ₹925 Cr additional revenue to maintain margins', 'Material cost has 3x higher impact on margins compared to FOREX movements']} data-id="element-4477" />
      {/* Scenario Slicer */}
      <div className="mb-8" data-id="element-4478">
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-4479">
          Select Scenario
        </h2>
        <div className="flex space-x-4" data-id="element-4480">
          {scenarios.map(scenario => <button key={scenario} onClick={() => setSelectedScenario(scenario)} className={`flex-1 py-4 px-6 rounded-xl text-lg font-bold transition-all ${selectedScenario === scenario ? scenario === 'Optimistic' ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg scale-105' : scenario === 'Base' ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg scale-105' : 'bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-4481">
              {scenario}
            </button>)}
        </div>
      </div>
      {/* KPIs for Selected Scenario */}
      <div className="mb-8" data-id="element-4482">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-4483">
          {selectedScenario} Scenario Impact
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4484">
          <Card className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-4485">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-4486">
              Material Cost Impact
            </div>
            <div className="text-xs text-gray-400 mb-3" data-id="element-4487">
              {currentKPIs.materialImpact.subtitle}
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-4488">
              {currentKPIs.materialImpact.value}
            </div>
            <div className={`text-sm flex items-center font-semibold ${currentKPIs.materialImpact.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-4489">
              {currentKPIs.materialImpact.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-4490" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-4491" />}
              {currentKPIs.materialImpact.impact} margin impact
            </div>
          </Card>
          <Card className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-4492">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-4493">
              FOREX Impact
            </div>
            <div className="text-xs text-gray-400 mb-3" data-id="element-4494">
              {currentKPIs.forexImpact.subtitle}
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-4495">
              {currentKPIs.forexImpact.value}
            </div>
            <div className={`text-sm flex items-center font-semibold ${currentKPIs.forexImpact.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-4496">
              {currentKPIs.forexImpact.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-4497" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-4498" />}
              {currentKPIs.forexImpact.impact} margin impact
            </div>
          </Card>
          <Card className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-4499">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-4500">
              Resulting EBITDA Margin
            </div>
            <div className="text-xs text-gray-400 mb-3" data-id="element-4501">
              {currentKPIs.marginSensitivity.subtitle}
            </div>
            <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-4502">
              {currentKPIs.marginSensitivity.value}
            </div>
            <div className={`text-sm flex items-center font-semibold ${currentKPIs.marginSensitivity.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-4503">
              {currentKPIs.marginSensitivity.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-4504" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-4505" />}
              {currentKPIs.marginSensitivity.impact} vs current
            </div>
          </Card>
        </div>
      </div>
      {/* Margin Sensitivity Curve and Cost Shock Impact */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-4506">
        {/* Margin Sensitivity Curve */}
        <Card className="p-6" data-id="element-4507">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4508">
            Margin Sensitivity Curve
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4509">
            EBITDA margin vs material cost change (%)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-4510">
            <LineChart data={marginSensitivityData} data-id="element-4511">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4512" />
              <XAxis dataKey="materialCostChange" tick={{
              fontSize: 11
            }} label={{
              value: 'Material Cost Change (%)',
              position: 'insideBottom',
              offset: -5,
              style: {
                fontSize: 11
              }
            }} data-id="element-4513" />
              <YAxis tick={{
              fontSize: 12
            }} domain={[14, 21]} label={{
              value: 'EBITDA Margin (%)',
              angle: -90,
              position: 'insideLeft',
              style: {
                fontSize: 11
              }
            }} data-id="element-4514" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-4515" />
              <Line type="monotone" dataKey="ebitdaMargin" stroke="#3b82f6" strokeWidth={4} name="EBITDA Margin %" dot={{
              r: 5,
              fill: '#3b82f6'
            }} data-id="element-4516" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4517">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4518">
              <div className="text-center" data-id="element-4519">
                <div className="text-gray-600" data-id="element-4520">Best Case</div>
                <div className="text-lg font-bold text-green-600" data-id="element-4521">20.3%</div>
                <div className="text-xs text-gray-500" data-id="element-4522">-5% material cost</div>
              </div>
              <div className="text-center" data-id="element-4523">
                <div className="text-gray-600" data-id="element-4524">Current</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-4525">18.3%</div>
                <div className="text-xs text-gray-500" data-id="element-4526">0% change</div>
              </div>
              <div className="text-center" data-id="element-4527">
                <div className="text-gray-600" data-id="element-4528">Worst Case</div>
                <div className="text-lg font-bold text-red-600" data-id="element-4529">15.3%</div>
                <div className="text-xs text-gray-500" data-id="element-4530">+10% material cost</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Cost Shock Impact Bar Chart */}
        <Card className="p-6" data-id="element-4531">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4532">
            Cost Shock Impact by Scenario
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4533">
            Impact on key financial metrics (%)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-4534">
            <BarChart data={costShockImpactData} data-id="element-4535">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4536" />
              <XAxis dataKey="scenario" tick={{
              fontSize: 11
            }} data-id="element-4537" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-4538" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value > 0 ? '+' : ''}${value.toFixed(1)}%`} data-id="element-4539" />
              <Legend data-id="element-4540" />
              <Bar dataKey="revenueImpact" fill="#3b82f6" name="Revenue Impact %" data-id="element-4541" />
              <Bar dataKey="costImpact" fill="#f59e0b" name="Cost Impact %" data-id="element-4542" />
              <Bar dataKey="ebitdaImpact" fill="#10b981" name="EBITDA Impact %" data-id="element-4543" />
              <Bar dataKey="patImpact" fill="#8b5cf6" name="PAT Impact %" data-id="element-4544" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4545">
            <div className="flex items-center justify-center space-x-2 text-sm" data-id="element-4546">
              <AlertCircleIcon size={16} className="text-yellow-600" data-id="element-4547" />
              <span className="text-gray-600" data-id="element-4548">
                Base scenario assumes 0% revenue growth with 5% material
                inflation
              </span>
            </div>
          </div>
        </Card>
      </div>
      {/* Scenario Result Summary Table */}
      <Card className="p-6 mb-8" data-id="element-4549">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4550">
          Scenario Result Summary - FY24 Projections
        </h3>
        <div className="overflow-x-auto" data-id="element-4551">
          <table className="w-full" data-id="element-4552">
            <thead data-id="element-4553">
              <tr className="border-b-2 border-gray-300" data-id="element-4554">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4555">
                  Scenario
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4556">
                  Material Cost
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4557">
                  FOREX Rate
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4558">
                  Revenue
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4559">
                  COGS
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4560">
                  Gross Profit
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4561">
                  Gross %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4562">
                  EBITDA
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4563">
                  EBITDA %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4564">
                  PAT
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4565">
                  PAT %
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4566">
              {scenarioResultsData.map((row, index) => <tr key={index} className={`border-b border-gray-100 hover:bg-gray-50 ${row.scenario === selectedScenario ? 'bg-blue-50 font-semibold' : ''}`} data-id="element-4567">
                  <td className="py-3 px-4" data-id="element-4568">
                    <div className="flex items-center" data-id="element-4569">
                      <div className="w-3 h-3 rounded mr-2" style={{
                    backgroundColor: row.color
                  }} data-id="element-4570" />
                      <span className="text-sm font-medium text-gray-800" data-id="element-4571">
                        {row.scenario}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4572">
                    +{row.materialCost}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4573">
                    ₹{row.forexRate}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-4574">
                    ₹{row.revenue} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-4575">
                    ₹{row.cogs} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-blue-600" data-id="element-4576">
                    ₹{row.grossProfit} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-blue-600" data-id="element-4577">
                    {row.grossMargin}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-green-600" data-id="element-4578">
                    ₹{row.ebitda} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-green-600" data-id="element-4579">
                    {row.ebitdaMargin}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-purple-600" data-id="element-4580">
                    ₹{row.pat} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-purple-600" data-id="element-4581">
                    {row.patMargin}%
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-4582">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-4583">
            <div className="text-center" data-id="element-4584">
              <div className="text-gray-600" data-id="element-4585">Revenue Range</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-4586">
                ₹8,954 - ₹9,481 Cr
              </div>
            </div>
            <div className="text-center" data-id="element-4587">
              <div className="text-gray-600" data-id="element-4588">EBITDA Range</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4589">
                ₹1,460 - ₹1,782 Cr
              </div>
            </div>
            <div className="text-center" data-id="element-4590">
              <div className="text-gray-600" data-id="element-4591">Margin Range</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-4592">
                16.3% - 18.8%
              </div>
            </div>
            <div className="text-center" data-id="element-4593">
              <div className="text-gray-600" data-id="element-4594">PAT Range</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-4595">
                ₹889 - ₹1,078 Cr
              </div>
            </div>
            <div className="text-center" data-id="element-4596">
              <div className="text-gray-600" data-id="element-4597">Scenario Spread</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-4598">₹322 Cr</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Detailed Assumptions Table */}
      <Card className="p-6" data-id="element-4599">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4600">
          Scenario Assumptions & Parameters
        </h3>
        <div className="overflow-x-auto" data-id="element-4601">
          <table className="w-full" data-id="element-4602">
            <thead data-id="element-4603">
              <tr className="border-b-2 border-gray-300" data-id="element-4604">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4605">
                  Parameter
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-green-700" data-id="element-4606">
                  Optimistic
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-blue-700" data-id="element-4607">
                  Base
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-red-700" data-id="element-4608">
                  Pessimistic
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4609">
                  Current Actual
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4610">
              {assumptionsData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-4611">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-4612">
                    {row.parameter}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4613">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700" data-id="element-4614">
                      {row.optimistic}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4615">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-4616">
                      {row.base}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4617">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700" data-id="element-4618">
                      {row.pessimistic}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4619">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700" data-id="element-4620">
                      {row.currentValue}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-4621">
          <div className="flex items-center justify-between" data-id="element-4622">
            <div className="flex items-center space-x-2" data-id="element-4623">
              <AlertCircleIcon size={20} className="text-blue-600" data-id="element-4624" />
              <span className="text-sm text-gray-600" data-id="element-4625">
                All scenarios assume constant product mix and no pricing changes
              </span>
            </div>
            <div className="text-sm text-gray-600" data-id="element-4626">
              Last updated: {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default ScenarioModellingDashboard;